import { useState, useRef, useCallback, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AlertCircle, RefreshCw, FolderPlus, FileText, GitBranch, Activity } from 'lucide-react';
import { InlineError } from '@/components/ui/inline-error.js';
import { SkeletonRow } from '@/components/ui/skeleton.js';
import { EmptyState } from '@/components/ui/empty-state.js';
import { FilterChipGroup } from '@/components/ui/filter-chip.js';
import {
  useGetSignalActivityChart,
  GetSignalActivityChartRange,
  customInstance,
  useSyncSignalPrd,
} from '@edison/api-client-react';
import type {
  GetSignalActivityChart200DataSeriesItem,
  GetSignalActivityChart200DataSeriesItemPointsItem,
} from '@edison/api-client-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RichSignal {
  id: string;
  type?: string;
  repoOwner: string;
  repoName: string;
  lastSyncedAt: string | null;
  projectId: string | null;
  projectName: string | null;
  lastCommitAt: string | null;
  lastCommitBy: string | null;
  firstCommitAt: string | null;
  contributorCount: number;
  artefactCount: number;
  hasPrd: boolean;
  active: boolean;
}

interface RichSignalsResponse {
  data: { items: RichSignal[] };
}

// ---------------------------------------------------------------------------
// FY helpers
// ---------------------------------------------------------------------------

function currentFy(): string {
  const now = new Date();
  const year = now.getFullYear();
  return now.getMonth() >= 6 ? `FY${year}` : `FY${year - 1}`;
}

function fyOptions(): string[] {
  const fy = currentFy();
  const base = parseInt(fy.replace('FY', ''), 10);
  return Array.from({ length: 5 }, (_, i) => `FY${base - 3 + i}`);
}

/** Display an internal FY key (e.g. "FY2026") as Australian notation "FY25-26". */
function formatFy(fy: string): string {
  const year = parseInt(fy.replace('FY', ''), 10);
  if (isNaN(year)) return fy;
  return `FY${String(year - 1).slice(-2)}-${String(year).slice(-2)}`;
}

// ---------------------------------------------------------------------------
// Relative time helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / 3_600_000);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/** Short relative time including minutes — used for health stats bar. Returns null if no date. */
function formatShortRelativeTime(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ---------------------------------------------------------------------------
// Source type display names
// ---------------------------------------------------------------------------

const SOURCE_TYPE_LABELS: Record<string, string> = {
  github_webhook: 'GitHub',
  claude_code: 'Claude Code',
  linear: 'Linear',
  aws_cost: 'AWS Cost',
};

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

function signalStatus(lastCommitAt: string | null): {
  label: string;
  className: string;
} {
  if (!lastCommitAt) {
    return { label: 'Never synced', className: 'text-muted-foreground' };
  }
  const days = (Date.now() - new Date(lastCommitAt).getTime()) / 86_400_000;
  if (days <= 1) {
    return { label: 'Active', className: 'text-green-600 dark:text-green-400' };
  }
  if (days <= 7) {
    return {
      label: `Quiet (${Math.ceil(days)}d)`,
      className: 'text-amber-600 dark:text-amber-400',
    };
  }
  return {
    label: `Stale (${Math.floor(days)}d)`,
    className: 'text-red-600 dark:text-red-400',
  };
}

// ---------------------------------------------------------------------------
// Hook: rich signal sources
// ---------------------------------------------------------------------------

function useRichSignals() {
  return useQuery({
    queryKey: ['signals-rich'],
    queryFn: () =>
      customInstance<RichSignalsResponse>({
        url: '/api/signals',
        method: 'GET',
      }).then((r) => r.data.items),
  });
}

// ---------------------------------------------------------------------------
// Chart colors
// ---------------------------------------------------------------------------

const COLORS = [
  '#3b82f6',
  '#8b5cf6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#06b6d4',
  '#ec4899',
  '#84cc16',
] as const;

const SVG_WIDTH = 700;
const SVG_HEIGHT = 240;
const PAD = { top: 12, bottom: 28, left: 40, right: 8 };
const CHART_W = SVG_WIDTH - PAD.left - PAD.right;
const CHART_H = SVG_HEIGHT - PAD.top - PAD.bottom;

type Point = GetSignalActivityChart200DataSeriesItemPointsItem;
type Series = GetSignalActivityChart200DataSeriesItem;

function buildStackedPaths(
  series: Series[],
  showNonRd: boolean,
): {
  fillPaths: Array<{ path: string; color: string; id: string }>;
  strokePaths: Array<{ path: string; color: string }>;
  xLabels: Array<{ x: number; label: string }>;
  yLabels: Array<{ y: number; label: string }>;
  monthDividers: number[];
  dates: string[];
  maxVal: number;
  xOf: (i: number) => number;
  stackedTotalsPerDate: number[];
  seriesValuesPerDate: number[][];
} {
  // Collect and sort all unique dates
  const dateSet = new Set<string>();
  series.forEach((s) => s.points.forEach((p) => dateSet.add(p.date)));
  const dates = Array.from(dateSet).sort();

  if (dates.length === 0) {
    return { fillPaths: [], strokePaths: [], xLabels: [], yLabels: [], monthDividers: [], dates: [], maxVal: 0, xOf: () => 0, stackedTotalsPerDate: [], seriesValuesPerDate: [] };
  }

  const n = dates.length;

  // Build date → per-series value map
  const seriesValues = series.map((s) => {
    const map = new Map<string, Point>(s.points.map((p) => [p.date, p]));
    return dates.map((d) => {
      const p = map.get(d);
      if (!p) return 0;
      return showNonRd ? p.rdCount + p.nonRdCount : p.rdCount;
    });
  });

  // Compute stacked totals per date
  const stackedTotals = dates.map((_, di) =>
    seriesValues.reduce((sum, vals) => sum + (vals[di] ?? 0), 0),
  );
  const maxVal = Math.max(...stackedTotals, 1);

  const xOf = (i: number) => PAD.left + (i / Math.max(n - 1, 1)) * CHART_W;
  const yOf = (v: number) => PAD.top + CHART_H - (v / maxVal) * CHART_H;

  // Build area paths per series (stacked from bottom up)
  const cumulativeBases = dates.map(() => 0);
  const fillPaths: Array<{ path: string; color: string; id: string }> = [];
  const strokePaths: Array<{ path: string; color: string }> = [];

  series.forEach((s, si) => {
    const vals = seriesValues[si];
    if (!vals) return;
    const color = COLORS[si % COLORS.length] ?? COLORS[0];

    const tops = vals.map((v, di) => (cumulativeBases[di] ?? 0) + v);

    // Stroke path (top edge only)
    let stroke = '';
    tops.forEach((v, di) => {
      const x = n === 1 ? PAD.left + CHART_W / 2 : xOf(di);
      const y = yOf(v);
      stroke += di === 0 ? `M ${x},${y}` : ` L ${x},${y}`;
    });

    // Fill path (area: top edge + bottom edge reversed)
    let fill = stroke;
    // Close down to the base
    for (let di = tops.length - 1; di >= 0; di--) {
      const x = n === 1 ? PAD.left + CHART_W / 2 : xOf(di);
      const baseY = yOf(cumulativeBases[di] ?? 0);
      fill += ` L ${x},${baseY}`;
    }
    fill += ' Z';

    fillPaths.push({ path: fill, color, id: `grad-${si}` });
    strokePaths.push({ path: stroke, color });

    // Advance bases
    tops.forEach((v, di) => {
      cumulativeBases[di] = v;
    });
  });

  // X-axis labels (5 for day ranges, monthly for FY)
  const xLabels: Array<{ x: number; label: string }> = [];
  if (n <= 90) {
    // Show 5 evenly-spaced labels
    const step = Math.max(1, Math.floor((n - 1) / 4));
    for (let i = 0; i < n; i += step) {
      const d = dates[i];
      if (!d) continue;
      const x = n === 1 ? PAD.left + CHART_W / 2 : xOf(i);
      xLabels.push({ x, label: d.slice(5) }); // MM-DD
    }
  } else {
    // Monthly labels
    let lastMonth = '';
    dates.forEach((d, di) => {
      const month = d.slice(0, 7);
      if (month !== lastMonth) {
        lastMonth = month;
        const x = n === 1 ? PAD.left + CHART_W / 2 : xOf(di);
        xLabels.push({ x, label: new Date(d + 'T00:00:00').toLocaleDateString('en-AU', { month: 'short' }) });
      }
    });
  }

  // Month boundary dividers (for FY view)
  const monthDividers: number[] = [];
  if (n > 90) {
    let lastMonth = '';
    dates.forEach((d, di) => {
      const month = d.slice(0, 7);
      if (month !== lastMonth && di > 0) {
        monthDividers.push(xOf(di));
        lastMonth = month;
      } else if (di === 0) {
        lastMonth = month;
      }
    });
  }

  // Y-axis labels: 0, ~33%, ~66%, 100% of maxVal
  const yLabels: Array<{ y: number; label: string }> = [];
  const ySteps = maxVal <= 3 ? [0, 1, 2, maxVal] : [0, Math.round(maxVal * 0.33), Math.round(maxVal * 0.66), maxVal];
  const seen = new Set<number>();
  for (const v of ySteps) {
    if (!seen.has(v)) {
      seen.add(v);
      yLabels.push({ y: yOf(v), label: String(v) });
    }
  }

  return { fillPaths, strokePaths, xLabels, yLabels, monthDividers, dates, maxVal, xOf, stackedTotalsPerDate: stackedTotals, seriesValuesPerDate: seriesValues };
}

// ---------------------------------------------------------------------------
// SVG Chart
// ---------------------------------------------------------------------------

interface ChartProps {
  series: Series[];
  showNonRd: boolean;
}

function ActivityChart({ series, showNonRd }: ChartProps) {
  const { fillPaths, strokePaths, xLabels, yLabels, monthDividers, dates, xOf, stackedTotalsPerDate, seriesValuesPerDate } = buildStackedPaths(
    series,
    showNonRd,
  );

  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current || dates.length === 0) return;
    const rect = svgRef.current.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * SVG_WIDTH;
    const chartX = svgX - PAD.left;
    const idx = Math.round((chartX / CHART_W) * (dates.length - 1));
    setHoverIndex(Math.max(0, Math.min(dates.length - 1, idx)));
  }, [dates.length]);

  if (dates.length === 0) {
    const message = series.length === 0
      ? 'No commit activity yet — connect a repo to a project to start capturing signals'
      : 'No activity in this period';
    return (
      <div className="flex items-center justify-center h-48 rounded-lg border border-dashed text-sm text-muted-foreground">
        {message}
      </div>
    );
  }

  const hoverX = hoverIndex !== null && dates.length > 1 ? xOf(hoverIndex) : null;
  const hoverTotal = hoverIndex !== null ? (stackedTotalsPerDate[hoverIndex] ?? 0) : null;
  const hoverDate = hoverIndex !== null ? dates[hoverIndex] : null;

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
        width="100%"
        preserveAspectRatio="none"
        aria-label="Commit activity chart"
        role="img"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoverIndex(null)}
        style={{ cursor: 'crosshair' }}
      >
        <defs>
          {fillPaths.map(({ id, color }) => (
            <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.6} />
              <stop offset="100%" stopColor={color} stopOpacity={0.05} />
            </linearGradient>
          ))}
        </defs>

        {/* Y-axis grid lines */}
        {yLabels.map(({ y }, i) => (
          <line
            key={`yg-${i}`}
            x1={PAD.left}
            y1={y}
            x2={PAD.left + CHART_W}
            y2={y}
            stroke="currentColor"
            strokeOpacity={0.08}
            strokeWidth={1}
          />
        ))}

        {/* Month dividers */}
        {monthDividers.map((x, i) => (
          <line
            key={i}
            x1={x}
            y1={PAD.top}
            x2={x}
            y2={PAD.top + CHART_H}
            stroke="currentColor"
            strokeOpacity={0.12}
            strokeDasharray="3 3"
            strokeWidth={1}
          />
        ))}

        {/* Fill areas */}
        {fillPaths.map(({ path, id }) => (
          <path key={id} d={path} fill={`url(#${id})`} />
        ))}

        {/* Stroke lines */}
        {strokePaths.map(({ path, color }, i) => (
          <path
            key={i}
            d={path}
            fill="none"
            stroke={color}
            strokeWidth={1.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}

        {/* Hover vertical line */}
        {hoverX !== null && (
          <line
            x1={hoverX}
            y1={PAD.top}
            x2={hoverX}
            y2={PAD.top + CHART_H}
            stroke="currentColor"
            strokeOpacity={0.4}
            strokeWidth={1}
            strokeDasharray="3 2"
          />
        )}

        {/* Y-axis labels */}
        {yLabels.map(({ y, label }, i) => (
          <text
            key={`yl-${i}`}
            x={PAD.left - 4}
            y={y + 3}
            textAnchor="end"
            fontSize={8}
            fill="currentColor"
            opacity={0.45}
          >
            {label}
          </text>
        ))}

        {/* X-axis labels */}
        {xLabels.map(({ x, label }, i) => (
          <text
            key={i}
            x={x}
            y={SVG_HEIGHT - 4}
            textAnchor="middle"
            fontSize={9}
            fill="currentColor"
            opacity={0.5}
          >
            {label}
          </text>
        ))}
      </svg>

      {/* Hover tooltip */}
      {hoverIndex !== null && hoverDate && hoverTotal !== null && hoverX !== null && (
        <div
          className="pointer-events-none absolute top-0 z-10 rounded border bg-background shadow-sm px-2 py-1.5 text-xs"
          style={{
            left: `${Math.min(85, (hoverX / SVG_WIDTH) * 100)}%`,
            transform: (hoverX / SVG_WIDTH) > 0.7 ? 'translateX(-100%)' : 'translateX(0)',
            marginTop: 4,
          }}
        >
          <p className="font-medium text-foreground">{hoverDate}</p>
          {series.length > 1
            ? series.map((s, si) => {
                const v = seriesValuesPerDate[si]?.[hoverIndex] ?? 0;
                if (v === 0) return null;
                return (
                  <p key={si} className="text-muted-foreground flex items-center gap-1">
                    <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: COLORS[si % COLORS.length] }} />
                    {s.repoOwner}/{s.repoName}: <span className="font-medium text-foreground">{v}</span>
                  </p>
                );
              })
            : <p className="text-muted-foreground">Commits: <span className="font-medium text-foreground">{hoverTotal}</span></p>
          }
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Legend
// ---------------------------------------------------------------------------

function Legend({ series }: { series: Series[] }) {
  const visible = series.slice(0, 8);
  const overflow = series.length - visible.length;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      {visible.map((s, i) => (
        <span key={i} className="flex items-center gap-1">
          <span
            className="inline-block h-2.5 w-2.5 rounded-sm shrink-0"
            style={{ background: COLORS[i % COLORS.length] }}
            aria-hidden="true"
          />
          {s.repoOwner}/{s.repoName}
        </span>
      ))}
      {overflow > 0 && <span className="text-muted-foreground">+{overflow} more</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Activity table
// ---------------------------------------------------------------------------

const SOURCE_FILTERS: { key: string; label: string }[] = [
  { key: 'all', label: 'All sources' },
  { key: 'github_webhook', label: 'GitHub' },
];

function ActivityTable({
  items,
  isLoading,
  error,
  refetch,
  onBackfill,
  backfillingId,
}: {
  items: RichSignal[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  onBackfill: (id: string) => void;
  backfillingId: string | null;
}) {
  const syncPrd = useSyncSignalPrd();
  const [sourceFilter, setSourceFilter] = useState('all');

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} />)}
      </div>
    );
  }

  if (error) {
    return <InlineError message="Failed to load signal sources." onRetry={refetch} />;
  }

  const filteredSources = sourceFilter === 'all'
    ? items
    : items.filter(s => s.type === sourceFilter);

  return (
    <div className="space-y-3">
      <FilterChipGroup
        chips={SOURCE_FILTERS}
        value={sourceFilter}
        onChange={setSourceFilter}
      />

      {filteredSources.length === 0 ? (
        <EmptyState
          icon={<Activity className="h-10 w-10" />}
          title="No signal sources connected"
          body="Connect a GitHub repo to start capturing commit, PR, and issue signals."
        />
      ) : (
        <div className="max-h-[480px] overflow-y-auto rounded-[14px] border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-card">
              <tr className="border-b bg-muted/40">
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Repo</th>
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Project</th>
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground text-xs">PRD</th>
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground text-xs">Connected</th>
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Last sync</th>
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Last commit</th>
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">By</th>
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">First commit</th>
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Contributors</th>
                <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Artefacts</th>
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredSources.map((s) => {
                const { label, className } = signalStatus(s.lastCommitAt);
                return (
                  <tr key={s.id} className="hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs">
                      {s.repoOwner}/{s.repoName}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {s.projectName ?? <span className="opacity-40">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {s.hasPrd ? (
                        <span
                          className="inline-flex items-center text-emerald-600"
                          aria-label="PRD.md ingested"
                          title="PRD.md found and ingested"
                        >
                          <FileText className="h-3.5 w-3.5" aria-hidden="true" />
                        </span>
                      ) : (
                        <span className="group relative inline-flex items-center gap-1 text-muted-foreground/40">
                          <span aria-label="No PRD.md">—</span>
                          <button
                            type="button"
                            className="hidden group-hover:inline-flex items-center gap-1 text-xs text-primary hover:underline ml-1"
                            onClick={() => syncPrd.mutate({ id: s.id })}
                            disabled={syncPrd.isPending}
                            aria-label="Sync PRD.md from GitHub"
                          >
                            {syncPrd.isPending ? 'Syncing…' : 'Sync PRD'}
                          </button>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {s.projectName ? (
                        <span
                          className="inline-flex items-center text-emerald-600"
                          aria-label={`Connected to project ${s.projectName}`}
                          title={`Connected to: ${s.projectName}`}
                        >
                          <GitBranch className="h-3.5 w-3.5" aria-hidden="true" />
                        </span>
                      ) : (
                        <span className="text-muted-foreground/40" aria-label="Not connected to a project">—</span>
                      )}
                    </td>
                    <td className={`px-4 py-3 text-xs font-medium ${s.active ? className : 'text-muted-foreground'}`}>
                      {s.active ? (
                        <>
                          {label === 'Active' && (
                            <span
                              className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-green-500 align-middle"
                              aria-hidden="true"
                            />
                          )}
                          {label}
                        </>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400" title="Disabled — project archived">
                          <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-amber-500 align-middle" aria-hidden="true" />
                          Disabled
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {formatRelativeTime(s.lastSyncedAt)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {formatRelativeTime(s.lastCommitAt)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {s.lastCommitBy ?? <span className="opacity-40">—</span>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {s.firstCommitAt
                        ? new Date(s.firstCommitAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })
                        : <span className="opacity-40">—</span>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {s.contributorCount > 0 ? s.contributorCount : <span className="opacity-40">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{s.artefactCount}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline disabled:opacity-50"
                        onClick={() => onBackfill(s.id)}
                        disabled={backfillingId === s.id}
                        aria-label={`Backfill signal source ${s.repoOwner}/${s.repoName}`}
                      >
                        {backfillingId === s.id ? '…' : <RefreshCw className="inline h-3 w-3" aria-hidden="true" />}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

type RangeMode = '30d' | '60d' | '90d' | 'fy';

export function SignalSourcesPage() {
  const [range, setRange] = useState<RangeMode>('fy');
  const [selectedFy, setSelectedFy] = useState<string>(currentFy());
  const [showNonRd, setShowNonRd] = useState(false);
  const [fyMenuOpen, setFyMenuOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<string>('all');

  const chartParams =
    range === 'fy'
      ? { range: GetSignalActivityChartRange.fy, fy: selectedFy }
      : { range: GetSignalActivityChartRange[range] };

  const {
    data: chartData,
    isLoading: chartLoading,
    error: chartError,
    refetch: chartRefetchRaw,
  } = useGetSignalActivityChart(chartParams);

  // chartRefetchRaw is typed as `never` when TError=void in the generated hook;
  // cast to a plain void function to allow calling it from event handlers.
  const chartRefetch: () => void = chartRefetchRaw as unknown as () => void;

  const {
    data: signals,
    isLoading: signalsLoading,
    error: signalsError,
    refetch: signalsRefetch,
  } = useRichSignals();

  const createProjects = useMutation({
    mutationFn: () => customInstance<{ data: { created: number } }>({
      url: '/api/signals/create-projects',
      method: 'POST',
    }),
    onSuccess: () => void signalsRefetch(),
  });

  const backfill = useMutation({
    mutationFn: () => customInstance<{ data: { sourcesQueued: number; since: string } }>({
      url: '/api/signals/backfill',
      method: 'POST',
      data: {},  // empty = full history
    }),
    onSuccess: () => void signalsRefetch(),
  });

  const [backfillingId, setBackfillingId] = useState<string | null>(null);

  const signalBackfill = useMutation({
    mutationFn: (id: string) => {
      setBackfillingId(id);
      return customInstance<{ data: { sourcesQueued: number; since: string } }>({
        url: `/api/signals/${id}/backfill`,
        method: 'POST',
      });
    },
    onSuccess: () => {
      setBackfillingId(null);
      void signalsRefetch();
    },
    onError: () => {
      setBackfillingId(null);
    },
  });

  const series = chartData?.data?.series ?? [];

  // Unique projects derived from signal sources
  const uniqueProjects = useMemo(() => {
    if (!signals) return [];
    const seen = new Set<string>();
    const result: Array<{ projectId: string; projectName: string }> = [];
    for (const s of signals) {
      if (s.projectId && s.projectName && !seen.has(s.projectId)) {
        seen.add(s.projectId);
        result.push({ projectId: s.projectId, projectName: s.projectName });
      }
    }
    return result;
  }, [signals]);

  const projectFilterChips = useMemo(() => [
    { key: 'all', label: 'All projects' },
    ...uniqueProjects.map(p => ({
      key: p.projectId,
      label: p.projectName.length > 20 ? `${p.projectName.slice(0, 20)}…` : p.projectName,
    })),
  ], [uniqueProjects]);

  // Chart series filtered by selected project
  const filteredSeries = useMemo(() => {
    if (selectedProject === 'all' || !signals) return series;
    const repoKeys = new Set(
      signals
        .filter(s => s.projectId === selectedProject)
        .map(s => `${s.repoOwner}/${s.repoName}`),
    );
    return series.filter(s => repoKeys.has(`${s.repoOwner}/${s.repoName}`));
  }, [series, selectedProject, signals]);

  // Table rows filtered by selected project
  const filteredSignals = useMemo(() => {
    if (selectedProject === 'all' || !signals) return signals ?? [];
    return signals.filter(s => s.projectId === selectedProject);
  }, [signals, selectedProject]);

  // Health stats: recent activity, last sync, distinct source types
  const healthStats = useMemo(() => {
    if (!signals || signals.length === 0) return null;
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    let recent7d = 0;
    let lastSyncDate: string | null = null;
    const types = new Set<string>();
    for (const s of signals) {
      const dateStr = s.lastSyncedAt ?? s.lastCommitAt;
      if (dateStr && new Date(dateStr).getTime() >= cutoff) recent7d++;
      if (s.lastSyncedAt && (!lastSyncDate || s.lastSyncedAt > lastSyncDate)) {
        lastSyncDate = s.lastSyncedAt;
      }
      if (s.type) types.add(s.type);
    }
    return {
      recent7d,
      lastSyncRelative: formatShortRelativeTime(lastSyncDate),
      distinctTypes: Array.from(types),
    };
  }, [signals]);

  const rangeButtons: Array<{ key: RangeMode; label: string }> = [
    { key: '30d', label: '30d' },
    { key: '60d', label: '60d' },
    { key: '90d', label: '90d' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Signals</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Raw activity captured from your tools, classified into R&D automatically.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {backfill.data?.data && (
            <span className="text-xs text-muted-foreground">
              Queued backfill for {backfill.data.data.sourcesQueued} repos
            </span>
          )}
          <button
            onClick={() => backfill.mutate()}
            disabled={backfill.isPending}
            className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-accent disabled:opacity-50"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            {backfill.isPending ? 'Backfilling…' : 'Backfill FY2025-26'}
          </button>
          {createProjects.data?.data?.created !== undefined && (
            <span className="text-xs text-muted-foreground">
              {createProjects.data.data.created === 0
                ? 'All repos have projects'
                : `${createProjects.data.data.created} project${createProjects.data.data.created !== 1 ? 's' : ''} created`}
            </span>
          )}
          <button
            onClick={() => createProjects.mutate()}
            disabled={createProjects.isPending}
            className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-accent disabled:opacity-50"
          >
            <FolderPlus className="h-3.5 w-3.5" aria-hidden="true" />
            {createProjects.isPending ? 'Creating…' : 'Create Projects'}
          </button>
        </div>
      </div>

      {/* Project filter chips — only when 2+ distinct projects */}
      {!signalsLoading && uniqueProjects.length >= 2 && (
        <FilterChipGroup
          chips={projectFilterChips}
          value={selectedProject}
          onChange={setSelectedProject}
        />
      )}

      {/* Health stats row */}
      {!signalsLoading && healthStats && (
        <div className="rounded-[14px] border border-border bg-card px-4 py-3 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {healthStats.distinctTypes.map(type => (
              <span
                key={type}
                className="flex items-center gap-1.5 text-xs text-foreground bg-muted border border-border px-3 py-1.5 rounded-full"
              >
                <span
                  className="w-1.5 h-1.5 rounded-full bg-[oklch(0.55_0.14_155)]"
                  aria-hidden="true"
                />
                {SOURCE_TYPE_LABELS[type] ?? type}
              </span>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-4">
            <div className="text-right">
              <p className="font-mono text-base font-semibold text-foreground">{healthStats.recent7d}</p>
              <p className="text-xs text-muted-foreground">signals · 7d</p>
            </div>
            {healthStats.lastSyncRelative && (
              <div className="text-right">
                <p className="font-mono text-base font-semibold text-foreground">{healthStats.lastSyncRelative}</p>
                <p className="text-xs text-muted-foreground">since sync</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Chart card */}
      <div className="rounded-lg border bg-card p-4 space-y-3">
        {/* Controls row */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          {/* Range buttons + FY dropdown */}
          <div className="flex items-center gap-1">
            {rangeButtons.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setRange(key)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  range === key
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                }`}
              >
                {label}
              </button>
            ))}
            {/* FY dropdown */}
            <div className="relative ml-1">
              <button
                onClick={() => {
                  setFyMenuOpen((v) => !v);
                  setRange('fy');
                }}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1 ${
                  range === 'fy'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                }`}
              >
                {formatFy(selectedFy)}
                <svg
                  className="h-3 w-3 opacity-60"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.085l3.71-3.854a.75.75 0 111.08 1.04l-4.25 4.42a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {fyMenuOpen && (
                <div className="absolute left-0 top-full mt-1 z-10 w-28 rounded-md border bg-popover shadow-md py-1">
                  {fyOptions().map((fy) => (
                    <button
                      key={fy}
                      onClick={() => {
                        setSelectedFy(fy);
                        setRange('fy');
                        setFyMenuOpen(false);
                      }}
                      className={`w-full px-3 py-1.5 text-left text-xs hover:bg-accent transition-colors ${
                        selectedFy === fy && range === 'fy' ? 'font-semibold' : ''
                      }`}
                    >
                      {formatFy(fy)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* All activity toggle */}
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
            <span>Show all activity</span>
            <button
              role="switch"
              aria-checked={showNonRd}
              onClick={() => setShowNonRd((v) => !v)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                showNonRd ? 'bg-primary' : 'bg-muted-foreground/30'
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transform transition-transform ${
                  showNonRd ? 'translate-x-[18px]' : 'translate-x-[3px]'
                }`}
              />
            </button>
          </label>
        </div>

        {/* Legend */}
        {filteredSeries.length > 0 ? <Legend series={filteredSeries} /> : null}

        {/* Chart subtitle */}
        <p className="text-xs text-muted-foreground">
          {showNonRd
            ? 'All captured activity — includes pending, unreviewed, and non-R&D entries'
            : 'Confirmed R&D work only — approved time entries with R&D allocation > 0%'}
        </p>

        {/* Chart */}
        {chartLoading ? (
          <div className="h-40 animate-pulse rounded-lg bg-muted" />
        ) : null}
        {!chartLoading && chartError ? (
          <div className="flex items-center gap-2 text-destructive text-sm py-4">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>Failed to load chart data.</span>
            <button
              type="button"
              onClick={chartRefetch}
              className="rounded-lg border bg-card px-2.5 py-1 text-xs font-medium inline-flex items-center gap-1 hover:bg-secondary transition-colors"
            >
              <RefreshCw className="h-3 w-3" aria-hidden="true" />
              Retry
            </button>
          </div>
        ) : null}
        {!chartLoading && !chartError && (
          <ActivityChart series={filteredSeries} showNonRd={showNonRd} />
        )}
      </div>

      {/* Activity table */}
      <ActivityTable
        items={filteredSignals}
        isLoading={signalsLoading}
        error={signalsError instanceof Error ? signalsError : signalsError ? new Error(String(signalsError)) : null}
        refetch={() => signalsRefetch()}
        onBackfill={(id) => signalBackfill.mutate(id)}
        backfillingId={backfillingId}
      />
    </div>
  );
}
